'use client';
import { useMemoizedFn, useRequest } from 'ahooks';
import type { Options, Service } from 'ahooks/lib/useRequest/src/types';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@/app/components/ui/use-toast';
import { getRequestPlatform, IS_PROD, NETWORK_SETTINGS } from '@/app/constants';
import type { ServiceError } from '@/app/types';
import { reportEvent } from '@/app/utils';
import {
  addAwsWafToken,
  getFetchErrorMsg,
  getUsingLanguage,
  handleAwsWafError,
  handleAwsWafResponse,
  hysdk,
} from '@/app/utils/client';
import { usePathname, useRouter } from '@/i18n/routing';
import { useStoreApi } from '../providers/store';
import { client } from '../services/client';

interface UseFetchOptions<TData, TParams extends unknown[]> extends Options<TData, TParams> {
  suppressUnauthorizedLoginDialog?: boolean;
}

client.setConfig(NETWORK_SETTINGS);

client.interceptors.request.use(async (request) => {
  // 设置语言
  request.headers.set('Accept-Language', getUsingLanguage());
  request.headers.set('X-Platform', request.headers.get('X-Platform') ?? getRequestPlatform());
  request.headers.set('X-App-Version', `${hysdk.sniff.appVersion}-${hysdk.sniff.codeVersion}`);

  if (IS_PROD) {
    // 从 cookie 中读取 backend_canary(后端灰度标识) 标识，如果存在则设置请求头
    const backendCanary = document.cookie
      .split('; ')
      .find((row) => row.startsWith('backend_canary='))
      ?.split('=')[1];

    if (backendCanary === 'true') {
      request.headers.set('X-Backend-Canary', 'true');
    }
  }

  if (request.method === 'POST' || request.method === 'PATCH') {
    const requestId = uuidv4();
    request.headers.set('X-Request-Id', requestId);
  }

  // 使用统一的 AWS WAF token 处理函数
  await addAwsWafToken(request.headers, request.url);

  return request;
});

client.interceptors.response.use(async (response, request) => {
  // 使用统一的 AWS WAF 响应处理函数
  return handleAwsWafResponse(response, request.url);
});

export function useFetch<TData, TParams extends unknown[]>(
  service: Service<TData, TParams>,
  options?: UseFetchOptions<TData, TParams>,
) {
  const t = useTranslations();
  const pathname = usePathname();
  const router = useRouter();
  const store = useStoreApi();

  // 创建一个 ref 来存储 req 对象，因为我们需要在 handleError 中访问它
  const reqRef = useRef<ReturnType<typeof useRequest> | null>(null);

  // 捕获网络层错误、请求中止、AWS WAF 拦截及 interceptor 主动抛出的错误
  // 注意: HTTP 4xx/5xx 状态码不会进入此处,会在 onSuccess 中通过 data?.error 处理
  const handleError = useMemoizedFn(async (error: Error, params: TParams) => {
    const handled = await handleAwsWafError(
      error,
      () => reqRef.current?.refresh(),
      () => {
        toast({
          type: 'error',
          message: t('popup.toast.awsWafChallengeError'),
        });
      },
    );
    if (handled) {
      return;
    }

    // 如果有自定义的错误处理函数，则调用自定义的错误处理函数
    if (options?.onError) {
      options.onError(error, params);
      return;
    }
    const msg = getFetchErrorMsg(error, t);
    toast({
      type: 'error',
      message: msg,
    });
  });

  // 使用 useMemoizedFn 确保函数引用稳定
  const handleSuccess = useMemoizedFn((data: TData, params: TParams) => {
    // 如果有自定义的成功处理函数，则调用自定义的成功处理函数

    // @ts-expect-error
    if (data?.error) {
      // @ts-expect-error
      const error = data?.error as ServiceError;
      // @ts-expect-error
      const requestUrl = (data?.request?.url as string | undefined) ?? '';
      const isMeRequest = requestUrl.includes('/v1/users/me');
      const { updateUserInfo, updateCreditsInfo, updateSubscriptionInfo, isUserInfoInitialized } = store.getState();
      // 此处如果接口返回的错误是未授权，且当前页面不是登录页，且无用户信息，则打开登录弹窗。
      // 如果出现部分接口返回未授权，但是有用户信息，则不跳转，解决因为部分接口异常导致的页面反复跳转的问题
      if (
        error.reason === 'Unauthorized' &&
        !isMeRequest &&
        !options?.suppressUnauthorizedLoginDialog &&
        pathname !== '/login' &&
        !pathname.startsWith('/subscription/') &&
        isUserInfoInitialized
      ) {
        updateUserInfo(null);
        updateCreditsInfo(null);
        updateSubscriptionInfo(null);
        store.getState().openModal('loginDialog', { redirectUrl: pathname });
        reportEvent('login_pop_exposure', {
          refer_element: `Page Address (${pathname})`,
          refer_page: pathname,
        });
        return;
      }
      if (error.reason === 'UserBlocked' && pathname !== '/banned' && pathname !== '/login') {
        // 被封禁的用户 userInfo 设为 null，并跳转到封禁页面
        updateUserInfo(null);
        router.replace('/banned');
        return;
      }
    }

    if (options?.onSuccess) {
      options.onSuccess(data, params);
    }
  });

  const req = useRequest(service, {
    ...options,
    onError: handleError,
    onSuccess: handleSuccess,
  });

  // 将 req 对象存储到 ref 中，以便在 handleError 中访问
  reqRef.current = req;

  return req;
}
